/**
 * Reprint the card name for foils whose letters are foil in the preview:
 * Rare (silver), Ultra (light gold on the gold plate), Ghost (holographic silver).
 * The printed ink is erased once per picture and cached. The typeface is
 * DejaVu Serif Condensed, a stand-in for the commercial card face.
 */

import type { CardFoilId } from '@shared/index'
import { englishName } from '../i18n/localizeCard'
import { BOARD_TEX_H, BOARD_TEX_W, downsampleCard } from '../game/phaserCardTexture'
import { getLocalCardImageUrl } from './cardImages'
import { loadCardImage, peekCardImage } from './cardImageCache'

export type FoilNameKind = 'silver' | 'ultra' | 'ghost'

/** Pale silver art. CardArt and the Nítida board image share this string. */
export const GHOST_ART_FILTER =
  'grayscale(0.88) brightness(1.42) contrast(0.76) sepia(0.08)'

const NAME_KIND: Partial<Record<CardFoilId, FoilNameKind>> = {
  rare: 'silver',
  ultra: 'ultra',
  ghost: 'ghost',
}

const FONT_FAMILY = '"Card Name", "Times New Roman", serif'
const FONT_URL = '/card-name/DejaVuSerifCondensed.ttf'
const NAME_PX_W = 740
/** Cap-height box. The canvas is taller so a J hook is not clipped. */
const NAME_LAYOUT_H = 52
const NAME_PX_H = 68

export interface FoilNamePart {
  text: string
  small: boolean
}

export function foilNameKind(foil: CardFoilId | null): FoilNameKind | null {
  if (!foil) return null
  return NAME_KIND[foil] ?? null
}

/** First letter of each word full size; the rest smaller capitals. */
export function foilNameParts(name: string): FoilNamePart[] {
  const parts: FoilNamePart[] = []
  let small = ''
  const flush = (): void => {
    if (!small) return
    parts.push({ text: small, small: true })
    small = ''
  }
  for (const ch of name) {
    if (ch >= 'A' && ch <= 'Z') {
      flush()
      parts.push({ text: ch, small: false })
    } else if (ch === ' ') {
      flush()
      parts.push({ text: ' ', small: false })
    } else {
      small += ch.toUpperCase()
    }
  }
  flush()
  return parts
}

function clamp(value: number, max: number): number {
  if (value < 0) return 0
  if (value >= max) return max - 1
  return value
}

function rowAverage(px: Uint8ClampedArray, w: number, h: number, x0: number, count: number, y: number): Float32Array {
  const out = new Float32Array(count * 3)
  for (let i = 0; i < count; i++) {
    const x = x0 + i
    const o = i * 3
    for (let k = 0; k < 3; k++) {
      const row = clamp(y + k, h) * w
      out[o] += px[(row + x) * 4]
      out[o + 1] += px[(row + x) * 4 + 1]
      out[o + 2] += px[(row + x) * 4 + 2]
    }
    out[o] /= 3
    out[o + 1] /= 3
    out[o + 2] /= 3
  }
  return out
}

/** Drops a descender that lands on the sample row. The plate gradient stays. */
function medianRow(samples: Float32Array, count: number, radius: number): Float32Array {
  const out = new Float32Array(count * 3)
  for (let i = 0; i < count; i++) {
    const from = Math.max(0, i - radius)
    const to = Math.min(count - 1, i + radius)
    for (let c = 0; c < 3; c++) {
      const vals: number[] = []
      for (let j = from; j <= to; j++) vals.push(samples[j * 3 + c] ?? 0)
      vals.sort((a, b) => a - b)
      out[i * 3 + c] = vals[vals.length >> 1] ?? 0
    }
  }
  return out
}

function sampleDistance(a: Float32Array, b: Float32Array, i: number): number {
  const o = i * 3
  return Math.abs((a[o] ?? 0) - (b[o] ?? 0))
    + Math.abs((a[o + 1] ?? 0) - (b[o + 1] ?? 0))
    + Math.abs((a[o + 2] ?? 0) - (b[o + 2] ?? 0))
}

/**
 * Rebuild the name plate from the rows above and below the ink.
 * A J hook drops past the letter band and into the lower sample, which
 * used to paint the black letter back under the foil name.
 */
export function eraseNamePixels(image: ImageData): void {
  const { width: w, height: h, data: px } = image
  const x0 = Math.round(0.068 * w)
  const x1 = Math.min(w, Math.round(0.828 * w))
  const y0 = Math.round(0.056 * h)
  const y1 = Math.min(h, Math.round(0.101 * h))
  const ya = Math.round(0.050 * h)
  const yb = Math.round(0.102 * h)
  if (x1 <= x0 || y1 <= y0) return
  const count = x1 - x0
  const span = Math.max(1, yb - ya)
  const rawBelow = rowAverage(px, w, h, x0, count, yb)
  const radius = Math.max(4, Math.round(0.018 * w))
  const above = medianRow(rowAverage(px, w, h, x0, count, ya), count, radius)
  const below = medianRow(rawBelow, count, radius)
  const dilate = Math.max(6, Math.round(0.028 * w))
  const hook = new Uint8Array(count)
  for (let i = 0; i < count; i++) {
    if (sampleDistance(rawBelow, below, i) <= 70) continue
    const from = Math.max(0, i - dilate)
    const to = Math.min(count - 1, i + dilate)
    for (let j = from; j <= to; j++) hook[j] = 1
  }
  const paint = (x: number, y: number, t: number): void => {
    const sx = x < x0 ? x0 : x >= x1 ? x1 - 1 : x
    const o = (sx - x0) * 3
    const i = (y * w + x) * 4
    const ar = above[o] ?? 0
    const ag = above[o + 1] ?? 0
    const ab = above[o + 2] ?? 0
    const br = below[o] ?? 0
    const bg = below[o + 1] ?? 0
    const bb = below[o + 2] ?? 0
    px[i] = ar * (1 - t) + br * t
    px[i + 1] = ag * (1 - t) + bg * t
    px[i + 2] = ab * (1 - t) + bb * t
  }
  for (let y = y0; y < y1; y++) {
    const t = (y - ya) / span
    for (let x = x0; x < x1; x++) paint(x, y, t)
  }
  // The hook curls onto the light left edge. Fill it from that edge,
  // not from the darker plate, or the corner keeps a dark spot.
  const yHook = Math.min(h, Math.round(0.1094 * h))
  const xHook = Math.round(0.055 * w)
  const stem = hook[0] === 1
  const read = (x: number, y: number): [number, number, number] | null => {
    if (x < 0 || x >= w || y < 0 || y >= h) return null
    const i = (y * w + x) * 4
    const r = px[i] ?? 0
    const g = px[i + 1] ?? 0
    const b = px[i + 2] ?? 0
    const lum = r + g + b
    if (lum < 280 || lum > 700) return null
    return [r, g, b]
  }
  const innerStep = Math.max(8, Math.round(0.02 * w))
  const donorFor = (x: number, y: number): [number, number, number] | null => {
    const innerX = Math.min(w - 1, x + innerStep)
    for (let dy = 1; dy <= 18; dy++) {
      const here = read(x, y - dy)
      if (!here) continue
      const inner = read(innerX, y - dy)
      const hereLum = here[0] + here[1] + here[2]
      const innerLum = inner ? inner[0] + inner[1] + inner[2] : hereLum
      if (hereLum > innerLum + 50) continue
      return here
    }
    for (let d = 1; d <= 28; d++) {
      const side = read(x - d, y)
      if (side) return side
    }
    return null
  }
  for (let y = y1; y < yHook; y++) {
    for (let x = xHook; x < x1; x++) {
      const i = x - x0
      if (i >= 0 && hook[i] !== 1) continue
      if (i < 0 && !stem) continue
      const pi = (y * w + x) * 4
      const r = px[pi] ?? 0
      const g = px[pi + 1] ?? 0
      const b = px[pi + 2] ?? 0
      const donor = donorFor(x, y)
      if (!donor) continue
      const delta = Math.abs(r - donor[0]) + Math.abs(g - donor[1]) + Math.abs(b - donor[2])
      if (delta < 70) continue
      px[pi] = donor[0]
      px[pi + 1] = donor[1]
      px[pi + 2] = donor[2]
    }
  }
}

const erasedUrl = new Map<string, Promise<string>>()

/**
 * iOS Safari paints a black bitmap when several full-card canvases are
 * allocated together, and then caches that JPEG. One plate at a time.
 */
let plateTail: Promise<void> = Promise.resolve()

function enqueuePlate<T>(run: () => Promise<T>): Promise<T> {
  const next = plateTail.then(run, run)
  plateTail = next.then(() => undefined, () => undefined)
  return next
}

/**
 * A failed drawImage on iOS is a black or empty canvas, not an exception.
 * The card frame is never black, so a blank border means the plate is unusable.
 */
function plateIsBlank(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
): boolean {
  const spots: Array<[number, number]> = [
    [0.5, 0.02],
    [0.02, 0.5],
    [0.98, 0.5],
    [0.5, 0.98],
    [0.5, 0.45],
  ]
  let ink = 0
  for (const [xf, yf] of spots) {
    const x = Math.min(width - 1, Math.max(0, Math.round(xf * (width - 1))))
    const y = Math.min(height - 1, Math.max(0, Math.round(yf * (height - 1))))
    const pixel = ctx.getImageData(x, y, 1, 1).data
    if (pixel[3] > 16 && pixel[0] + pixel[1] + pixel[2] > 24) ink += 1
  }
  return ink < 2
}

function plateKey(src: string, ghost: boolean): string {
  return ghost ? `ghost:${src}` : src
}

/** Pale-silver JPEG. The pixels carry the wash so a canvas hole cannot strip it. */
function bakeGhostJpeg(img: HTMLImageElement): string | null {
  try {
    const width = img.naturalWidth
    const height = img.naturalHeight
    if (width < 8 || height < 8) return null
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.filter = GHOST_ART_FILTER
    ctx.drawImage(img, 0, 0)
    ctx.filter = 'none'
    if (plateIsBlank(ctx, width, height)) return null
    const url = canvas.toDataURL('image/jpeg', 0.92)
    return url.startsWith('data:image/') ? url : null
  } catch {
    return null
  }
}

function bakeErasedJpeg(src: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      try {
        const width = img.naturalWidth
        const height = img.naturalHeight
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        if (!ctx || width < 8 || height < 8) {
          resolve(src)
          return
        }
        ctx.drawImage(img, 0, 0)
        if (plateIsBlank(ctx, width, height)) {
          resolve(src)
          return
        }
        const image = ctx.getImageData(0, 0, width, height)
        eraseNamePixels(image)
        ctx.putImageData(image, 0, 0)
        const url = canvas.toDataURL('image/jpeg', 0.92)
        resolve(url.startsWith('data:image/') ? url : src)
      } catch {
        resolve(src)
      }
    }
    img.onerror = () => resolve(src)
    img.src = src
  })
}

function bakeGhostFrom(src: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(bakeGhostJpeg(img) ?? src)
    img.onerror = () => resolve(src)
    img.src = src
  })
}

/**
 * One JPEG per picture. Later copies reuse the data URL.
 * `ghost` washes the art to pale silver in the bitmap (Nítida board, 3D overlay).
 * Vue CardArt keeps the CSS filter and must pass `ghost: false`.
 */
export function eraseNameUrl(src: string, ghost = false): Promise<string> {
  const key = plateKey(src, ghost)
  const hit = erasedUrl.get(key)
  if (hit) return hit
  const job = ghost
    ? eraseNameUrl(src, false).then((plain) => enqueuePlate(() => bakeGhostFrom(plain)))
    : enqueuePlate(() => bakeErasedJpeg(src))
  erasedUrl.set(key, job)
  return job
}

const STOPS: Record<FoilNameKind, Array<[number, string]>> = {
  silver: [[0, '#6b7280'], [0.42, '#f8fafc'], [0.58, '#9ca3af'], [1, '#e5e7eb']],
  ultra: [[0, '#fff7d6'], [0.28, '#ffffff'], [0.46, '#ffe9a0'], [0.62, '#fffdf5'], [1, '#f3d27a']],
  ghost: [[0, '#64748b'], [0.18, '#ffffff'], [0.36, '#cbd5e1'], [0.5, '#f8fafc'], [0.68, '#94a3b8'], [0.84, '#ffffff'], [1, '#64748b']],
}

export function drawFoilName(
  ctx: CanvasRenderingContext2D,
  name: string,
  kind: FoilNameKind,
  width: number,
  height: number,
): void {
  const parts = foilNameParts(name)
  const fontPx = NAME_LAYOUT_H * (0.0526 / 0.052)
  ctx.clearRect(0, 0, width, height)
  ctx.textBaseline = 'alphabetic'
  const widthAt = (scale: number): number => {
    let total = 0
    for (const part of parts) {
      const size = (part.small ? fontPx * 0.76 : fontPx) * scale
      ctx.font = `400 ${size}px ${FONT_FAMILY}`
      total += ctx.measureText(part.text).width
    }
    return total
  }
  const natural = widthAt(1)
  const scale = natural > width ? width / natural : 1
  const cap = fontPx * scale * 0.77
  const baseline = NAME_LAYOUT_H / 2 + cap / 2
  const grad = ctx.createLinearGradient(0, 0, width, 0)
  for (const [stop, color] of STOPS[kind]) grad.addColorStop(stop, color)
  ctx.fillStyle = grad
  let x = 0
  for (const part of parts) {
    const size = (part.small ? fontPx * 0.76 : fontPx) * scale
    ctx.font = `400 ${size}px ${FONT_FAMILY}`
    ctx.fillText(part.text, x, baseline)
    x += ctx.measureText(part.text).width
  }
}

let fontReady = false
let fontPromise: Promise<void> | null = null

function ensureNameFont(): Promise<void> {
  if (fontReady) return Promise.resolve()
  if (!fontPromise) {
    const face = new FontFace('Card Name', `url(${FONT_URL})`)
    fontPromise = face.load().then((loaded) => {
      document.fonts.add(loaded)
      fontReady = true
    }).catch(() => undefined)
  }
  return fontPromise
}

void ensureNameFont()

interface DrawnName {
  scene: Phaser.Scene
  key: string
  canvas: HTMLCanvasElement
  name: string
  kind: FoilNameKind
}

const drawnNames: DrawnName[] = []

function paintName(entry: DrawnName): void {
  const ctx = entry.canvas.getContext('2d')
  if (!ctx) return
  drawFoilName(ctx, entry.name, entry.kind, entry.canvas.width, entry.canvas.height)
  try {
    if (!entry.scene.textures.exists(entry.key)) return
    const tex = entry.scene.textures.get(entry.key) as Phaser.Textures.CanvasTexture
    tex.update()
  } catch {
    // The scene dropped the texture with the board.
  }
}

void ensureNameFont().then(() => {
  if (!fontReady) return
  for (const entry of drawnNames) paintName(entry)
})

function blankKey(cardId: string, ghost: boolean): string {
  return ghost ? `foil-ghost-${cardId}` : `foil-noname-${cardId}`
}

function nameKey(cardId: string, kind: FoilNameKind): string {
  return `foil-name-${cardId}-${kind}`
}

/**
 * Erase the name on the full scan, then bake the Estándar face.
 * The 128×184 board texture is too small: the sample rows land inside
 * the letters and the ink is painted back.
 */
function renderErasedBoard(
  img: HTMLImageElement,
  ghost: boolean,
): HTMLCanvasElement | null {
  const sw = img.naturalWidth || img.width
  const sh = img.naturalHeight || img.height
  if (sw < 8 || sh < 8) return null
  const full = document.createElement('canvas')
  full.width = sw
  full.height = sh
  const ctx = full.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null
  ctx.drawImage(img, 0, 0)
  const image = ctx.getImageData(0, 0, sw, sh)
  eraseNamePixels(image)
  ctx.putImageData(image, 0, 0)
  const out = document.createElement('canvas')
  out.width = BOARD_TEX_W
  out.height = BOARD_TEX_H
  const outCtx = out.getContext('2d')
  if (!outCtx) return null
  outCtx.imageSmoothingEnabled = true
  outCtx.imageSmoothingQuality = 'high'
  if (ghost) outCtx.filter = GHOST_ART_FILTER
  downsampleCard(outCtx, full, BOARD_TEX_W, BOARD_TEX_H)
  return out
}

function applyErasedFace(
  scene: Phaser.Scene,
  face: Phaser.GameObjects.Image,
  cardId: string,
  cardW: number,
  cardH: number,
  ghost: boolean,
): void {
  const cleared = blankKey(cardId, ghost)
  const use = (): void => {
    if (!face.active || !scene.textures.exists(cleared)) return
    face.setTexture(cleared)
    face.setDisplaySize(cardW, cardH)
  }
  if (scene.textures.exists(cleared)) {
    use()
    return
  }
  const url = getLocalCardImageUrl(cardId)
  const ready = peekCardImage(url)
  if (ready) {
    const canvas = renderErasedBoard(ready, ghost)
    if (canvas) scene.textures.addCanvas(cleared, canvas)
    use()
    return
  }
  void loadCardImage(url).then((loaded) => {
    try {
      if (!face.active) return
      if (!scene.textures.exists(cleared)) {
        const canvas = renderErasedBoard(loaded, ghost)
        if (canvas) scene.textures.addCanvas(cleared, canvas)
      }
      use()
    } catch {
      // The scene dropped the texture with the board.
    }
  }).catch(() => undefined)
}

/**
 * Board cards share one texture, so the blank plate is a second texture.
 * Nítida punches a hole and shows the JPG behind it; that plate is erased
 * in the page, and this stamp would only paint a few dark pixels on top.
 * The name is a still gradient; the open card animates its letters in Vue.
 */
export function stampFoilName(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  foil: CardFoilId,
  cardW: number,
  cardH: number,
  face?: Phaser.GameObjects.Image,
  cardId?: string,
): void {
  const kind = foilNameKind(foil)
  if (!kind || !face || !cardId) return
  if (face.getData('sharpErase')) return
  const name = englishName(cardId)
  if (!name) return
  try {
    applyErasedFace(scene, face, cardId, cardW, cardH, kind === 'ghost')

    const key = nameKey(cardId, kind)
    if (!scene.textures.exists(key)) {
      const canvas = document.createElement('canvas')
      canvas.width = NAME_PX_W
      canvas.height = NAME_PX_H
      const entry: DrawnName = { scene, key, canvas, name, kind }
      paintName(entry)
      scene.textures.addCanvas(key, canvas)
      drawnNames.push(entry)
    }
    const boxW = cardW * 0.74
    const boxH = cardH * 0.052 * (NAME_PX_H / NAME_LAYOUT_H)
    const x = face.x - cardW / 2 + cardW * 0.075 + boxW / 2
    const y = face.y - cardH / 2 + cardH * 0.0485 + boxH / 2
    container.add(
      scene.add.image(x, y, key).setDisplaySize(boxW, boxH),
    )
  } catch {
    // Keep the printed name if the plate cannot be rebuilt.
  }
}
